import React, {
  useState,
  useContext,
  useMemo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react'

import { View } from 'react-native'

import type { LayoutRectangle } from 'react-native'
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
  interpolateColor,
} from 'react-native-reanimated'

import {
  XStack,
  YStack,
  ThemeableStack,
  styled,
  AnimatePresence,
  TamaguiElement,
  useThemeName,
  useMedia,
  View as TamaguiView,
} from 'tamagui'

import { useLocation } from 'react-router-dom'

import { ServerContext } from '@data/ServerContext'

import { Text } from '../Text'
import { Button } from '../Button/Button'
import { Icon } from '../Icon'

import { Item, DynamicIslandRowsContext } from './DynamicIslandProvider'
import { canHover } from './canHover'

import './DynamicIsland.css'

const SPRING_OPTIONS = { mass: 0.15, stiffness: 60, damping: 5 }

type WebLayoutRectangle = LayoutRectangle & {
  left: number
  top: number
}

const useIsomorphicLayoutEffect = __SERVER__ ? useEffect : useLayoutEffect

const layoutEqual = (
  a: WebLayoutRectangle | undefined,
  b: WebLayoutRectangle | undefined
): boolean =>
  a === b ||
  (!!a &&
    !!b &&
    a.x === b.x &&
    a.y === b.y &&
    a.width === b.width &&
    a.height === b.height &&
    a.left === b.left &&
    a.top === b.top)

export const DynamicIsland = ({ isLoading }: { isLoading: boolean }) => {
  const containerRef = useRef<View>(null)
  const dynamicIslandRows = useContext(DynamicIslandRowsContext)
  const { reducedMotion: reducedEffects } = useMedia()

  const [contentBackgroundPosition, setContentBackgroundPosition] = useState<
    WebLayoutRectangle | undefined
  >()

  const [backgroundPosition, setBackgroundPosition] = useState<
    WebLayoutRectangle | undefined
  >(contentBackgroundPosition)

  const [appliedBackgroundPosition, setAppliedBackgroundPosition] = useState<
    WebLayoutRectangle | undefined
  >(undefined)

  const serverContext = useContext(ServerContext)

  const backgroundLeft = useSharedValue(0)
  const backgroundTop = useSharedValue(0)
  // Use matchMedia directly instead of useMedia() — the styled component's
  // $gtXs compiles to a CSS media query for the visual width, and this
  // value just seeds the Reanimated shared value to match.
  const initialWidth =
    (serverContext && serverContext.defaultBreakpoint > 0) ||
    (!serverContext &&
      typeof window !== 'undefined' &&
      window.matchMedia('(min-width: 577px)').matches)
      ? 407
      : 237
  const backgroundWidth = useSharedValue(initialWidth)
  const backgroundHeight = useSharedValue(48)

  // Track content width as a shared value so we can compute centering
  // offsets in animated styles (content width changes instantly on reflow,
  // background width springs — the offset keeps the background centered)
  const contentWidthValue = useSharedValue(initialWidth)

  const animatedContentStyle = useAnimatedStyle(() => {
    if (contentBackgroundPosition) {
      // Clamp bottom inset to prevent clipping content during height
      // spring transitions (when backgroundHeight is still catching up
      // to the measured contentBackgroundPosition.height).
      const rawBottomInset = -(
        backgroundHeight.value -
        contentBackgroundPosition.height -
        1
      )
      const bottomInset = Math.min(rawBottomInset, 1)

      const centerOffset = (contentWidthValue.value - backgroundWidth.value) / 2
      const visualLeft = backgroundLeft.value + centerOffset

      return {
        clipPath: `inset(1px calc(100% - ${visualLeft + backgroundWidth.value - 2}px) ${bottomInset}px ${visualLeft + 1}px)`,
        willChange: 'clip-path,transform',
      }
    } else {
      return {
        clipPath: 'inset(0px 0px 0px 0px)',
        willChange: 'clip-path,transform',
      }
    }
  }, [contentBackgroundPosition, backgroundPosition])

  const backgroundStyle = useAnimatedStyle(() => {
    const centerOffset = (contentWidthValue.value - backgroundWidth.value) / 2

    return {
      width: backgroundWidth.value,
      height: backgroundHeight.value,
      transform: [
        { translateX: backgroundLeft.value + centerOffset },
        { translateY: backgroundTop.value },
      ],
    }
  }, [contentBackgroundPosition, backgroundPosition])

  const dynamicIslandView = useRef<TamaguiElement>(null)

  // --- Stack-based row content transitions ---
  // Content layers form a stack: base content at bottom, overlays pushed on top.
  // PUSH: new content cube-rotates in, previous content fades & scales out.
  // POP:  top content cube-rotates out (reverse), revealed content fades & scales in.
  const rowKey = (rows: Array<Array<Item>>) =>
    rows.map((r) => r.map((i) => i.id).join(',')).join('|')

  // The full stack of row content. Index 0 = base, last = currently displayed.
  const rowStackRef = useRef<Array<{ rows: Array<Array<Item>>; key: string }>>([
    { rows: dynamicIslandRows.rows, key: rowKey(dynamicIslandRows.rows) },
  ])

  // Rendered layers
  const [displayRows, setDisplayRows] = useState(dynamicIslandRows.rows)
  const [underRows, setUnderRows] = useState<Array<Array<Item>> | null>(null)
  const [exitingRows, setExitingRows] = useState<Array<Array<Item>> | null>(
    null
  )

  // Captured widths for under/exit layers so they keep their original size
  // during transitions instead of squishing to the new display width.
  const underRowWidthShared = useSharedValue(0)
  const exitRowWidthShared = useSharedValue(0)

  const displayRowKey = displayRows
    .map((row) => row.map((item) => item.id).join(','))
    .join('|')

  // Display layer shared values (cube rotation on push, fade+scale on pop reveal)
  const displayRotateX = useSharedValue(0)
  const displayOpacity = useSharedValue(1)
  const displayScale = useSharedValue(1)

  // Under layer shared values (fade+scale when covered/revealed)
  const underOpacity = useSharedValue(0)
  const underScale = useSharedValue(0.9)

  // Exit layer shared values (cube rotation out on pop)
  const exitRotateX = useSharedValue(0)
  const exitOpacity = useSharedValue(1)

  const CUBE_SPRING = { mass: 0.4, stiffness: 200, damping: 18 }
  const FADE_SPRING = { mass: 0.3, stiffness: 300, damping: 24 }

  const clearExiting = useCallback(() => {
    setExitingRows(null)
  }, [])

  const clearUnder = useCallback(() => {
    setUnderRows(null)
  }, [])

  // Detect context row changes → push or pop.
  // Uses useIsomorphicLayoutEffect so state updates (setDisplayRows etc.)
  // trigger a synchronous re-render BEFORE the browser paints, ensuring
  // the measurement layout effect fires with the correct displayRowKey
  // before anything is visible on screen.
  useIsomorphicLayoutEffect(() => {
    const newKey = rowKey(dynamicIslandRows.rows)
    const stack = rowStackRef.current
    const topKey = stack.length > 0 ? stack[stack.length - 1].key : ''

    if (newKey === topKey) return // no change

    // Check if newKey exists in the stack (POP back to that level)
    const existingIndex = stack.findIndex((e) => e.key === newKey)

    if (existingIndex >= 0) {
      // POP: returning to a previous stack level
      const poppedEntry = stack[stack.length - 1]

      // Truncate stack to the matched level
      rowStackRef.current = stack.slice(0, existingIndex + 1)
      const newStack = rowStackRef.current

      // Capture current width so exit layer keeps its original size
      exitRowWidthShared.value = contentWidthValue.value

      // Set layers
      setExitingRows(poppedEntry.rows)
      setDisplayRows(newStack[newStack.length - 1].rows)
      setUnderRows(
        newStack.length >= 2 ? newStack[newStack.length - 2].rows : null
      )

      // Animate exit (cube rotation out — reverse of entry)
      exitRotateX.value = 0
      exitOpacity.value = 1
      exitRotateX.value = withSpring(-90, CUBE_SPRING, (finished) => {
        if (finished) {
          runOnJS(clearExiting)()
        }
      })
      exitOpacity.value = withSpring(0, CUBE_SPRING)

      // Animate display (fade & scale in from hidden)
      displayRotateX.value = 0
      displayOpacity.value = 0
      displayScale.value = 0.9
      displayOpacity.value = withSpring(1, FADE_SPRING)
      displayScale.value = withSpring(1, FADE_SPRING)
    } else {
      // PUSH: new content overlaying
      if (topKey === '') {
        // Initial population
        rowStackRef.current = [{ rows: dynamicIslandRows.rows, key: newKey }]
        setDisplayRows(dynamicIslandRows.rows)
        return
      }

      // Save previous top as under layer and push new content
      const prevTop = stack[stack.length - 1]
      stack.push({ rows: dynamicIslandRows.rows, key: newKey })

      // Capture current width so under layer keeps its original size
      underRowWidthShared.value = contentWidthValue.value

      setUnderRows(prevTop.rows)
      setDisplayRows(dynamicIslandRows.rows)

      // Animate under (fade & scale out, then clean up)
      underOpacity.value = 1
      underScale.value = 1
      underOpacity.value = withSpring(0, FADE_SPRING, (finished) => {
        if (finished) {
          runOnJS(clearUnder)()
        }
      })
      underScale.value = withSpring(0.9, FADE_SPRING)

      // Animate display (cube rotation in)
      displayScale.value = 1
      displayRotateX.value = -90
      displayOpacity.value = 0
      displayRotateX.value = withSpring(0, CUBE_SPRING)
      displayOpacity.value = withSpring(1, CUBE_SPRING)
    }
  }, [dynamicIslandRows.rows, clearExiting, clearUnder])

  // Display layer style: cube rotation (push) or fade+scale (pop reveal)
  const displayTransformStyle = useAnimatedStyle(() => ({
    transform: [
      { perspective: 800 },
      { rotateX: `${displayRotateX.value}deg` },
      { scale: displayScale.value },
    ],
    opacity: displayOpacity.value,
  }))

  // Under layer style: fade & scale, with captured width so it doesn't squish.
  // Includes its own clip-path that uses capturedWidth (not contentWidthValue)
  // for the center offset. This compensates for the translateX centering: the
  // clip-path is applied in pre-transform space, so the offset must match where
  // the background pill appears relative to the un-translated element position.
  const underFadeStyle = useAnimatedStyle(() => {
    const capturedWidth = underRowWidthShared.value
    const centerX =
      capturedWidth > 0 ? (contentWidthValue.value - capturedWidth) / 2 : 0

    if (contentBackgroundPosition && capturedWidth > 0) {
      const centerOffset = (capturedWidth - backgroundWidth.value) / 2
      const visualLeft = backgroundLeft.value + centerOffset
      const rawBottomInset = -(
        backgroundHeight.value -
        contentBackgroundPosition.height -
        1
      )
      const bottomInset = Math.min(rawBottomInset, 1)
      return {
        clipPath: `inset(1px calc(100% - ${visualLeft + backgroundWidth.value - 2}px) ${bottomInset}px ${visualLeft + 1}px)`,
        willChange: 'clip-path,transform',
        opacity: underOpacity.value,
        width: capturedWidth,
        transform: [{ translateX: centerX }, { scale: underScale.value }],
      }
    }

    return {
      clipPath: 'inset(0px 0px 0px 0px)',
      willChange: 'clip-path,transform',
      opacity: underOpacity.value,
      width: capturedWidth > 0 ? capturedWidth : 'auto',
      transform: [{ translateX: centerX }, { scale: underScale.value }],
    }
  }, [contentBackgroundPosition, backgroundPosition])

  // Exit layer style: cube rotation out, with captured width so it doesn't squish.
  // Same clip-path adjustment as the under layer.
  const exitTransformStyle = useAnimatedStyle(() => {
    const capturedWidth = exitRowWidthShared.value
    const centerX =
      capturedWidth > 0 ? (contentWidthValue.value - capturedWidth) / 2 : 0

    if (contentBackgroundPosition && capturedWidth > 0) {
      const centerOffset = (capturedWidth - backgroundWidth.value) / 2
      const visualLeft = backgroundLeft.value + centerOffset
      const rawBottomInset = -(
        backgroundHeight.value -
        contentBackgroundPosition.height -
        1
      )
      const bottomInset = Math.min(rawBottomInset, 1)
      return {
        clipPath: `inset(1px calc(100% - ${visualLeft + backgroundWidth.value - 2}px) ${bottomInset}px ${visualLeft + 1}px)`,
        willChange: 'clip-path,transform',
        width: capturedWidth,
        transform: [
          { translateX: centerX },
          { perspective: 800 },
          { rotateX: `${exitRotateX.value}deg` },
        ],
        opacity: exitOpacity.value,
      }
    }

    return {
      clipPath: 'inset(0px 0px 0px 0px)',
      willChange: 'clip-path,transform',
      width: capturedWidth > 0 ? capturedWidth : 'auto',
      transform: [
        { translateX: centerX },
        { perspective: 800 },
        { rotateX: `${exitRotateX.value}deg` },
      ],
      opacity: exitOpacity.value,
    }
  }, [contentBackgroundPosition, backgroundPosition])

  useIsomorphicLayoutEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const handler = () => {
      requestAnimationFrame(() => {
        if (dynamicIslandView.current) {
          // On web, TamaguiElement is an HTMLElement with DOM methods
          const element = dynamicIslandView.current as unknown as HTMLElement
          const contentLayout = element.getBoundingClientRect()
          const parentLayout = (
            element.parentNode as HTMLElement | null
          )?.getBoundingClientRect()

          const dynamicIslandPosition = {
            x: contentLayout.x - (parentLayout?.x || 0),
            y: contentLayout.y - (parentLayout?.y || 0),
            width: contentLayout.width,
            height: contentLayout.height,
            left: contentLayout.left,
            top: contentLayout.top,
          }

          setContentBackgroundPosition((prev) =>
            layoutEqual(prev, dynamicIslandPosition)
              ? prev
              : dynamicIslandPosition
          )
          contentWidthValue.value = dynamicIslandPosition.width

          if (!backgroundPosition) {
            setAppliedBackgroundPosition((prev) =>
              layoutEqual(prev, dynamicIslandPosition)
                ? prev
                : dynamicIslandPosition
            )

            backgroundWidth.set(dynamicIslandPosition.width)
            backgroundHeight.set(dynamicIslandPosition.height)
          }
        }
      })
    }

    if (!contentBackgroundPosition) {
      handler()
    }

    window.addEventListener('resize', handler)

    return () => {
      window.removeEventListener('resize', handler)
    }
  }, [backgroundPosition, contentBackgroundPosition])

  // Shared measure function used by both the layout effect and ResizeObserver.
  const measureAndUpdate = useCallback(() => {
    if (!dynamicIslandView.current) return

    const element = dynamicIslandView.current as unknown as HTMLElement
    const contentLayout = element.getBoundingClientRect()
    const parentLayout = (
      element.parentNode as HTMLElement | null
    )?.getBoundingClientRect()

    const dynamicIslandPosition = {
      x: contentLayout.x - (parentLayout?.x || 0),
      y: contentLayout.y - (parentLayout?.y || 0),
      width: contentLayout.width,
      height: contentLayout.height,
      left: contentLayout.left,
      top: contentLayout.top,
    }

    setContentBackgroundPosition((prev) =>
      layoutEqual(prev, dynamicIslandPosition) ? prev : dynamicIslandPosition
    )
    setAppliedBackgroundPosition((prev) =>
      layoutEqual(prev, dynamicIslandPosition) ? prev : dynamicIslandPosition
    )
    contentWidthValue.value = dynamicIslandPosition.width

    backgroundLeft.value = withSpring(0, SPRING_OPTIONS)
    backgroundTop.value = withSpring(0, SPRING_OPTIONS)
    backgroundWidth.value = withSpring(
      dynamicIslandPosition.width,
      SPRING_OPTIONS
    )
    backgroundHeight.value = withSpring(
      dynamicIslandPosition.height,
      SPRING_OPTIONS
    )
  }, [
    backgroundHeight,
    backgroundLeft,
    backgroundTop,
    backgroundWidth,
    contentWidthValue,
  ])

  // Re-measure when rows change (e.g. flash messages added/removed).
  useIsomorphicLayoutEffect(() => {
    if (typeof window === 'undefined' || !dynamicIslandView.current) {
      return
    }

    // Reset any expand selection state
    setBackgroundPosition((prev) => (prev === undefined ? prev : undefined))

    measureAndUpdate()
    const rafId = requestAnimationFrame(measureAndUpdate)
    return () => cancelAnimationFrame(rafId)
  }, [displayRowKey])

  // ResizeObserver catches late layout changes (e.g. code-split icons
  // loading after the initial measurement).
  useIsomorphicLayoutEffect(() => {
    if (typeof window === 'undefined' || !dynamicIslandView.current) return
    if (typeof ResizeObserver === 'undefined') return

    const element = dynamicIslandView.current as unknown as HTMLElement

    const observer = new ResizeObserver(() => {
      measureAndUpdate()
    })

    observer.observe(element)
    return () => observer.disconnect()
  }, [measureAndUpdate])

  const [isHovered, setIsHovered] = useState(false)

  // Derive flash type from currently displayed rows for background color
  const displayFlashType = useMemo(() => {
    for (const row of displayRows) {
      for (const item of row) {
        if (item.meta?.flashType) return item.meta.flashType as string
      }
    }
    return null
  }, [displayRows])

  // Pre-hydration: return undefined so the styled component's CSS default
  // (var(--di-bg)) applies — .t_dark handles theme at the CSS level, no flash.
  // Post-hydration: switch to literal RGBA values so Tamagui/CSS can
  // interpolate colors for animations (flash, hover). The transition is
  // invisible because the CSS variable resolves to the same RGBA value.
  const [hydrated, setHydrated] = useState(false)
  useEffect(() => {
    setHydrated(true)
  }, [])

  const themeName = useThemeName()
  const isDark = themeName === 'dark'

  const isActive = isHovered || !!backgroundPosition

  // Animate background color via Reanimated so the transition is smooth.
  // CSS transitions on the styled component's `style` prop get overridden
  // by Reanimated's `style` prop, so we drive the color here instead.
  const bgColorProgress = useSharedValue(0)

  useEffect(() => {
    if (!hydrated) return
    bgColorProgress.value = withTiming(isActive ? 1 : 0, { duration: 280 })
  }, [bgColorProgress, hydrated, isActive])

  // Flash colors (success/error) override instantly
  const flashColor = displayFlashType
    ? { success: 'rgba(22, 163, 74, 0.92)', error: 'rgba(220, 38, 38, 0.92)' }[
        displayFlashType
      ]
    : undefined

  useEffect(() => {
    if (flashColor) {
      bgColorProgress.value = 2 // sentinel: flash active
    }
  }, [bgColorProgress, flashColor])

  const bgColorStyle = useAnimatedStyle(() => {
    if (!hydrated) return {}
    if (flashColor) return { backgroundColor: flashColor }

    const idle = isDark ? 'rgba(30,30,30,0.75)' : 'rgba(255,255,255,0.75)'
    const active = isDark ? 'rgba(30,30,30,0.9)' : 'rgba(255,255,255,0.9)'
    const color = interpolateColor(
      bgColorProgress.value,
      [0, 1],
      [idle, active]
    )
    return { backgroundColor: color }
  }, [hydrated, isDark, flashColor])

  // Promote to the browser's top layer so the island renders above native
  // <dialog> elements (which dialogs/sheets use via showModal()).
  useEffect(() => {
    const el = containerRef.current as unknown as HTMLElement | null
    if (el && typeof el.showPopover === 'function') {
      el.setAttribute('popover', 'manual')
      el.showPopover()
    }
  }, [])

  // Detect when a dialog/sheet overlay is open so we can blur behind the island.
  // Checks for 'dialog-blur-active' class on body, which is managed by
  // DialogSheet (position-aware) and DialogPortal (desktop view).
  const [dialogOpen, setDialogOpen] = useState(false)
  useEffect(() => {
    const check = () =>
      setDialogOpen(document.body.classList.contains('dialog-blur-active'))
    check()
    const observer = new MutationObserver(check)
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    })
    return () => observer.disconnect()
  }, [])

  const ignoreRowBackgroundPosition = useCallback(() => undefined, [])

  const handleRowBackgroundPosition = useCallback(
    (newBackgroundPosition?: WebLayoutRectangle) => {
      if (newBackgroundPosition) {
        const newPosition = newBackgroundPosition

        setBackgroundPosition((prev) =>
          layoutEqual(prev, newPosition) ? prev : newPosition
        )

        setAppliedBackgroundPosition((prev) =>
          layoutEqual(prev, newPosition) ? prev : newPosition
        )

        // Compensate for the centering offset so the background
        // lands at the correct visual position around the expand
        const centeringAdjust =
          ((contentBackgroundPosition?.width || 0) - newPosition.width) / 2

        backgroundTop.value = withSpring(newPosition.y, SPRING_OPTIONS)
        backgroundLeft.value = withSpring(
          newPosition.x - centeringAdjust,
          SPRING_OPTIONS
        )
        backgroundHeight.value = withSpring(newPosition.height, SPRING_OPTIONS)
        backgroundWidth.value = withSpring(newPosition.width, SPRING_OPTIONS)
      } else if (contentBackgroundPosition) {
        setBackgroundPosition((prev) => (prev === undefined ? prev : undefined))

        const element =
          dynamicIslandView.current as unknown as HTMLElement | null
        const contentLayout = element?.getBoundingClientRect()
        const parentLayout = (
          element?.parentNode as HTMLElement | null
        )?.getBoundingClientRect()

        const dynamicIslandPosition = {
          x: (contentLayout?.x ?? 0) - (parentLayout?.x || 0),
          y: (contentLayout?.y ?? 0) - (parentLayout?.y || 0),
          width: contentLayout?.width ?? 0,
          height: contentLayout?.height ?? 0,
          left: contentLayout?.left ?? 0,
          top: contentLayout?.top ?? 0,
        }

        setContentBackgroundPosition((prev) =>
          layoutEqual(prev, dynamicIslandPosition)
            ? prev
            : dynamicIslandPosition
        )
        contentWidthValue.value = dynamicIslandPosition.width

        const appliedPosition = {
          ...dynamicIslandPosition,
          x: 0,
          y: 0,
        }

        setAppliedBackgroundPosition((prev) =>
          layoutEqual(prev, appliedPosition) ? prev : appliedPosition
        )

        backgroundTop.value = withSpring(0, SPRING_OPTIONS)
        backgroundLeft.value = withSpring(0, SPRING_OPTIONS)
        backgroundHeight.value = withSpring(
          dynamicIslandPosition.height,
          SPRING_OPTIONS
        )
        backgroundWidth.value = withSpring(
          dynamicIslandPosition.width,
          SPRING_OPTIONS
        )
      } else {
        setBackgroundPosition((prev) => (prev === undefined ? prev : undefined))
        setAppliedBackgroundPosition((prev) =>
          prev === undefined ? prev : undefined
        )
      }
    },
    [
      backgroundHeight,
      backgroundLeft,
      backgroundTop,
      backgroundWidth,
      contentBackgroundPosition,
      contentWidthValue,
    ]
  )

  return (
    <>
      {/* Blur overlay rendered outside the popover so it stays in normal
          stacking context — above page content but below the sheet/dialog.
          Always mounted; backdrop-filter transitions to match dialog timing. */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          ...(reducedEffects
            ? {}
            : {
                backdropFilter: dialogOpen
                  ? 'blur(6px) saturate(1.5)'
                  : 'blur(0px) saturate(1)',
                WebkitBackdropFilter: dialogOpen
                  ? 'blur(6px) saturate(1.5)'
                  : 'blur(0px) saturate(1)',
                transition:
                  'backdrop-filter 250ms cubic-bezier(0, 0.5, 1, 1), -webkit-backdrop-filter 250ms cubic-bezier(0, 0.5, 1, 1)',
              }),
          pointerEvents: 'none',
          zIndex: 99998,
        }}
      />
      <DynamicIslandContainer ref={containerRef}>
        <TamaguiView
          ref={dynamicIslandView}
          onMouseEnter={() => {
            if (canHover) {
              setIsHovered(true)
            }
          }}
          onMouseLeave={() => {
            if (canHover) {
              setIsHovered(false)
            }
          }}
        >
          <DynamicIslandBackground style={[backgroundStyle, bgColorStyle]} />

          <AnimatePresence>
            {/* Under: previous content fading & scaling out when covered */}
            {underRows?.map((islandItems, index) => (
              <DynamicIslandRow
                key={`under-${index}`}
                gap='$2'
                alignItems='center'
                padding='$2'
                borderRadius={24}
                position='absolute'
                top={0}
                left={0}
                overflow='hidden'
                islandItems={islandItems}
                isLoading={isLoading}
                backgroundPosition={appliedBackgroundPosition}
                setBackgroundPosition={ignoreRowBackgroundPosition}
                style={[
                  underFadeStyle,
                  { pointerEvents: 'none', whiteSpace: 'nowrap' },
                ]}
              />
            ))}

            {/* Display: current top of stack (normal flow, determines layout) */}
            {displayRows.map((islandItems, index) => {
              return (
                <DynamicIslandRow
                  key={index}
                  gap='$2'
                  alignItems='center'
                  padding='$2'
                  borderRadius={24}
                  islandItems={islandItems}
                  isLoading={isLoading}
                  backgroundPosition={appliedBackgroundPosition}
                  setBackgroundPosition={handleRowBackgroundPosition}
                  style={[
                    animatedContentStyle,
                    displayTransformStyle,
                    {
                      transformOrigin: 'center top',
                      backfaceVisibility: 'hidden',
                    },
                  ]}
                />
              )
            })}

            {/* Exiting: top content cube-rotating out during pop */}
            {exitingRows?.map((islandItems, index) => (
              <DynamicIslandRow
                key={`exit-${index}`}
                gap='$2'
                alignItems='center'
                padding='$2'
                borderRadius={24}
                position='absolute'
                top={0}
                left={0}
                islandItems={islandItems}
                isLoading={isLoading}
                backgroundPosition={appliedBackgroundPosition}
                setBackgroundPosition={ignoreRowBackgroundPosition}
                style={[
                  exitTransformStyle,
                  {
                    transformOrigin: 'center top',
                    backfaceVisibility: 'hidden',
                    pointerEvents: 'none',
                  },
                ]}
              />
            ))}
          </AnimatePresence>
        </TamaguiView>
      </DynamicIslandContainer>
    </>
  )
}

const DynamicIslandRow = Animated.createAnimatedComponent(
  XStack.styleable<{
    islandItems: Array<Item>
    backgroundPosition?: WebLayoutRectangle
    isLoading: boolean
    setBackgroundPosition: (
      newBackgroundPosition?: WebLayoutRectangle
    ) => unknown
  }>(
    (
      {
        islandItems,
        backgroundPosition,
        setBackgroundPosition,
        isLoading,
        ...props
      },
      ref
    ) => {
      const location = useLocation()

      const [selectedItemId, setSelectedItemId] = useState<
        Item['id'] | undefined
      >()

      const [isHovered, setIsHovered] = useState(false)
      const setBackgroundPositionRef = useRef(setBackgroundPosition)

      useEffect(() => {
        setBackgroundPositionRef.current = setBackgroundPosition
      }, [setBackgroundPosition])

      // if we navigate to a new page, reset the dynamic island
      useEffect(() => {
        setSelectedItemId(undefined)
        setBackgroundPositionRef.current(undefined)
      }, [location.pathname, location.search, location.hash])

      useEffect(() => {
        if (selectedItemId && !isHovered) {
          const timeout = setTimeout(() => {
            setSelectedItemId(undefined)
            setBackgroundPosition(undefined)
          }, 5000)

          return () => {
            clearTimeout(timeout)
          }
        }
      }, [selectedItemId, isHovered, setBackgroundPosition])

      // Close the island when clicking outside it
      const islandRef = useRef<HTMLElement | null>(null)
      const mergedRef = useCallback(
        (node: HTMLElement | null) => {
          islandRef.current = node
          if (typeof ref === 'function') ref(node)
          else if (ref)
            (ref as React.MutableRefObject<HTMLElement | null>).current = node
        },
        [ref]
      )
      useEffect(() => {
        if (!selectedItemId) return
        const handlePointerDown = (e: MouseEvent) => {
          if (
            islandRef.current &&
            !islandRef.current.contains(e.target as Node)
          ) {
            setSelectedItemId(undefined)
            setBackgroundPosition(undefined)
          }
        }
        document.addEventListener('pointerdown', handlePointerDown)
        return () =>
          document.removeEventListener('pointerdown', handlePointerDown)
      }, [selectedItemId, setBackgroundPosition])

      const selectedItemIndex = useMemo(() => {
        const index = islandItems.findIndex(
          (item) => item.id === selectedItemId
        )

        if (index >= 0) {
          return index
        }
      }, [islandItems, selectedItemId])

      const selectedItem = useMemo(() => {
        if (selectedItemIndex === undefined) {
          return
        }

        return islandItems[selectedItemIndex]
      }, [islandItems, selectedItemIndex])

      return (
        <XStack
          {...props}
          onMouseEnter={() => {
            if (canHover) {
              setIsHovered(true)
            }
          }}
          onMouseLeave={() => {
            if (canHover) {
              setIsHovered(false)
            }
          }}
          ref={mergedRef}
        >
          <Text
            animation='dynamicIsland'
            fontWeight='$6'
            position='absolute'
            transform={[
              {
                translateX: selectedItem
                  ? (backgroundPosition?.x || 0) + (4 * 4) / 2
                  : 0,
              },
            ]}
            opacity={selectedItem ? 1 : 0}
            display={selectedItem ? 'flex' : 'none'}
            style={{ willChange: 'transform,opacity' }}
          >
            {selectedItem ? selectedItem.name : null}
          </Text>

          {islandItems.map((item, index) => {
            return (
              <DynamicIslandItem
                {...item}
                key={item.id}
                isLoading={isLoading}
                itemIndex={index}
                selectedItem={selectedItem}
                selectedItemIndex={selectedItemIndex}
                setSelectedItemId={setSelectedItemId}
                setBackgroundPosition={setBackgroundPosition}
              />
            )
          })}
        </XStack>
      )
    }
  )
)

const DynamicIslandItem = ({
  id,
  name,
  component,
  child,
  selectedItem,
  selectedItemIndex,
  setSelectedItemId,
  setBackgroundPosition,
  itemIndex,
  containerStyle = {},
  isLoading,
}: {
  isLoading: boolean
  selectedItem: Item | undefined
  selectedItemIndex: number | undefined
  setBackgroundPosition: (newBackgroundPosition?: WebLayoutRectangle) => unknown
  setSelectedItemId: (newSelectedItem: Item['id'] | undefined) => unknown
  itemIndex: number
} & Item) => {
  const [itemLayout, setItemLayout] = useState<WebLayoutRectangle>({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    left: 0,
    top: 0,
  })

  const [expandLayout, setExpandLayout] = useState<WebLayoutRectangle>({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    left: 0,
    top: 0,
  })

  const lastBgPosRef = useRef<WebLayoutRectangle | undefined>(undefined)

  // Increments each time this item is selected (opened). Used as a key on the
  // child content so React remounts it with fresh state on each open — resetting
  // ephemeral UI like the locale dropdown without affecting Relay cache.
  const openCountRef = useRef(0)
  const [openCount, setOpenCount] = useState(0)
  const isSelected = selectedItem?.id === id
  const prevSelectedRef = useRef(false)

  useEffect(() => {
    if (isSelected && !prevSelectedRef.current) {
      openCountRef.current++
      setOpenCount(openCountRef.current)
    }
    prevSelectedRef.current = isSelected
  }, [isSelected])

  const itemRef = useRef<TamaguiElement>(null)
  const closeItemRef = useRef<TamaguiElement>(null)

  // Drive close-button rotation & opacity with reanimated so they use the
  // exact same spring as the background (SPRING_OPTIONS / withSpring).
  const closeRotation = useSharedValue(0)
  const closeOpacity = useSharedValue(0)

  const closeButtonStyle = useAnimatedStyle(() => ({
    opacity: closeOpacity.value,
    transform: [{ rotateZ: `${closeRotation.value}deg` }],
  }))

  // Mirror rotation on the outgoing item so both icons rotate in tandem.
  const itemButtonStyle = useAnimatedStyle(() => ({
    opacity: 1 - closeOpacity.value,
    transform: [{ rotateZ: `${closeRotation.value}deg` }],
  }))

  // Decouple item opacity from transform so items fade in more gradually
  // than they move to position, preventing jarring overlaps during close.
  // Uses Reanimated directly because Tamagui/Moti per-property animation
  // overrides don't work (Moti sets transition: all 0s on web).
  const itemOpacity = useSharedValue(
    selectedItem === undefined || selectedItem?.id === id ? 1 : 0
  )

  useEffect(() => {
    const target = selectedItem === undefined || selectedItem?.id === id ? 1 : 0
    itemOpacity.value = withSpring(target, SPRING_OPTIONS)
  }, [selectedItem, id, itemOpacity])

  const itemOpacityStyle = useAnimatedStyle(() => ({
    opacity: itemOpacity.value,
  }))

  // Fixed offset that pushes all non-selected items uniformly out of the
  // Dynamic Island clip area. Every item on the same side moves the same
  // distance so the group exits as a cohesive unit.
  const DISMISS_OFFSET = 300

  const translateX = useMemo(() => {
    if (selectedItem === undefined || selectedItemIndex === undefined) {
      return 0
    }

    if (selectedItem.id === id) {
      return 0
    }

    // item is to the right of selection — push right
    if (selectedItemIndex < itemIndex) {
      return DISMISS_OFFSET
    }

    // item is to the left of selection — push left
    return -DISMISS_OFFSET
  }, [selectedItem, selectedItemIndex, id, itemIndex])

  const calculateBackgroundPosition = (
    itemLayout: LayoutRectangle & { top: number; left: number },
    expandLayout: LayoutRectangle & { top: number; left: number }
  ): LayoutRectangle & { top: number; left: number } => {
    return {
      top: itemLayout.top - 4 * 2,
      height: itemLayout.height + expandLayout.height + 4 * 2,

      left: itemLayout.left + itemLayout.width + 4 * 2 - expandLayout.width,
      width: expandLayout.width,

      x: itemLayout.x + itemLayout.width - expandLayout.width + (4 * 4) / 2,
      y: 0,
    }
  }

  // onLayout isn't reliable via Tamagui
  // Need to measure layout manually
  useIsomorphicLayoutEffect(() => {
    if (selectedItem?.id === id) {
      setItemLayout((prev) => {
        try {
          if (itemRef.current) {
            // On web, TamaguiElement is an HTMLElement with DOM methods
            const element = itemRef.current as unknown as HTMLElement
            const layout = element.getBoundingClientRect()
            const parentLayout = (
              element.parentNode as HTMLElement | null
            )?.getBoundingClientRect()

            const computedLayout = {
              x: layout.x - (parentLayout?.x || 0),
              y: layout.y - (parentLayout?.y || 0),
              width: layout.width,
              height: layout.height,
              left: layout.left,
              top: layout.top,
            }

            return layoutEqual(prev, computedLayout) ? prev : computedLayout
          }

          return prev
        } catch (err) {
          return prev
        }
      })
    }
  }, [selectedItem])

  useIsomorphicLayoutEffect(() => {
    if (selectedItem?.id === id) {
      if (itemLayout.width === 0 && itemLayout.height === 0) {
        setSelectedItemId(undefined)
        setBackgroundPosition(undefined)
      } else {
        const newBgPos = calculateBackgroundPosition(itemLayout, expandLayout)
        if (!layoutEqual(lastBgPosRef.current, newBgPos)) {
          lastBgPosRef.current = newBgPos
          setBackgroundPosition(newBgPos)
        }
        // Trigger close-button animation in the same layout pass as the
        // background spring so they move in perfect tandem.
        closeRotation.value = withSpring(45, SPRING_OPTIONS)
        closeOpacity.value = withSpring(1, SPRING_OPTIONS)
      }
    }
  }, [selectedItem, id, itemLayout, expandLayout])

  // Collapse close-button when deselected externally (navigation, timeout).
  // Double-triggering withSpring(0) when already collapsing is harmless.
  // Also clear lastBgPosRef so the next open forces a fresh setBackgroundPosition
  // call — otherwise layoutEqual on the stale ref skips the call and the pill
  // stays wide while selection is set (Profile label renders but bg never narrows).
  useIsomorphicLayoutEffect(() => {
    if (selectedItem?.id !== id) {
      closeRotation.value = withSpring(0, SPRING_OPTIONS)
      closeOpacity.value = withSpring(0, SPRING_OPTIONS)
      lastBgPosRef.current = undefined
    }
  }, [selectedItem, id])

  const renderedComponent = useMemo(() => {
    if (typeof component === 'function') {
      const Comp = component

      return <Comp isLoading={isLoading} />
    }

    return component
  }, [component, isLoading])

  if (name && child) {
    return (
      <>
        <YStack
          {...containerStyle}
          key={id}
          ref={itemRef}
          animation='dynamicIsland'
          animateOnly={['transform']}
          transform={[
            {
              translateX,
            },
          ]}
          cursor='pointer'
          onPress={() => {
            if (selectedItem?.id === id) {
              setSelectedItemId(undefined)
              setBackgroundPosition(undefined)
              // Collapse close-button in the same handler as the background
              closeRotation.value = withSpring(0, SPRING_OPTIONS)
              closeOpacity.value = withSpring(0, SPRING_OPTIONS)
            } else {
              setSelectedItemId(id)
              // setBackgroundPosition(
              //   calculateBackgroundPosition(itemLayout, expandLayout)
              // )
            }
          }}
          onLayout={(event) => {
            const layout = event.nativeEvent.layout
            const nextLayout = {
              ...layout,
              left: layout.x,
              top: layout.y,
            } as WebLayoutRectangle

            setItemLayout((prev) =>
              layoutEqual(prev, nextLayout) ? prev : nextLayout
            )
          }}
          style={{ willChange: 'transform,opacity' }}
        >
          <Animated.View style={itemOpacityStyle}>
            <Animated.View style={[itemButtonStyle]}>
              {renderedComponent}
            </Animated.View>

            <Animated.View
              style={[
                {
                  position: 'absolute',
                  pointerEvents: selectedItem?.id === id ? 'auto' : 'none',
                } as const,
                closeButtonStyle,
              ]}
            >
              <Button
                ref={closeItemRef}
                minHeight='$8'
                height='$8'
                width='$8'
                padding='$0'
                alignItems='center'
                justifyContent='center'
                secondary
              >
                <Icon name='add' color='$color' />
              </Button>
            </Animated.View>
          </Animated.View>
        </YStack>

        <YStack
          minWidth={120}
          animation='dynamicIsland'
          position='absolute'
          top={itemLayout.height + 2 * 4}
          transform={[
            {
              translateX:
                selectedItem?.id === id
                  ? itemLayout.x + itemLayout.width - expandLayout.width
                  : -itemLayout.width + 4 * 4,
            },
          ]}
          opacity={selectedItem?.id === id ? 1 : 0}
          paddingVertical='$2'
          paddingHorizontal='$4'
          // paddingTop='$2'
          // paddingBottom='$4'
          style={{ visibility: selectedItem?.id === id ? 'visible' : 'hidden' }}
          onLayout={(event) => {
            // console.log('EXPAND LAYOUT', event.nativeEvent.layout)
            const layout = event.nativeEvent.layout
            const nextLayout = {
              ...layout,
              left: layout.x,
              top: layout.y,
            } as WebLayoutRectangle

            setExpandLayout((prev) =>
              layoutEqual(prev, nextLayout) ? prev : nextLayout
            )
          }}
          // backgroundColor={selectedItem?.id === id ? 'purple' : undefined}
        >
          <React.Fragment key={openCount}>{child}</React.Fragment>
        </YStack>
      </>
    )
  }

  return (
    <YStack
      {...containerStyle}
      key={id}
      animation='dynamicIsland'
      animateOnly={['transform']}
      transform={[
        {
          translateX,
        },
      ]}
      onLayout={(event) => {
        const layout = event.nativeEvent.layout
        const nextLayout = {
          ...layout,
          left: layout.x,
          top: layout.y,
        } as WebLayoutRectangle

        setItemLayout((prev) =>
          layoutEqual(prev, nextLayout) ? prev : nextLayout
        )
      }}
    >
      <Animated.View style={itemOpacityStyle}>
        {renderedComponent}
      </Animated.View>
    </YStack>
  )
}

const DynamicIslandContainer = styled(View, {
  position: 'fixed',
  zIndex: 300_000,
  marginLeft: 'auto',
  marginRight: 'auto',
  width: '100%',
  alignItems: 'center',
  justifyContent: 'flex-start',

  style: {
    // Override popover UA defaults so the island keeps its layout
    // @ts-expect-error inset is a web-only CSS property
    inset: 'unset',
    top: 'calc(env(safe-area-inset-top, 0px) + 12px)',
    left: 0,
    right: 0,
    border: 'none',
    padding: 0,
    background: 'none',
    overflow: 'visible',
  },
})

const DynamicIslandBackground = Animated.createAnimatedComponent(
  styled(ThemeableStack, {
    backgroundColor: 'var(--di-bg)',
    // @ts-expect-error boxShadow is a web-only CSS property
    boxShadow:
      'rgba(0, 0, 0, 0.08) 0px 8px 8px -2px, rgba(0, 0, 0, 0.05) 0px 15px 25px 10px',
    position: 'absolute',
    borderRadius: 24,
    borderColor: 'rgba(128, 128, 128, 0.08)',
    borderWidth: 1,

    backdropFilter: 'blur(8px) saturate(250%)',

    // Initial dimensions matching mobile default; $gtXs compiles to a CSS
    // media query so desktop gets the correct width on first SSR paint
    // without waiting for JS hydration.
    width: 237,
    height: 48,
    $gtXs: {
      width: 407,
    },

    style: {
      pointerEvents: 'none',
      WebkitBackdropFilter: 'blur(8px) saturate(250%)',
      willChange: 'transform,width,height,background-color',
      // Spring easing matching the dynamicIsland Tamagui animation config
      // (mass: 0.15, stiffness: 60, damping: 5) expressed as CSS linear().
      transition:
        'background-color 280ms linear(0 0%, 0.034 5%, 0.115 10%, 0.221 15%, 0.336 20%, 0.449 25%, 0.555 30%, 0.648 35%, 0.729 40%, 0.797 45%, 0.852 50%, 0.895 55%, 0.929 60%, 0.955 65%, 0.974 70%, 0.987 75%, 0.997 80%, 1.003 85%, 1.006 90%, 1.008 95%, 1.009 100%)',
    },
  } as const)
)
